import React, { useState } from "react";
import Text from "../core/Text";
import { createStyleSheet } from "../react-native";
import Button from "../Button";
import InsuranceCardImage from "@assets/add-insurance-card.png";
import Flex from "../core/Flex";
import PhotoUploader from "@components/system/PhotoUploader";
import { FileData } from "@utils/fileUtils";
import { useGlobalContext } from "../../context/GlobalComponentsContext";
import { logEvent } from "@actions";
import { reportError } from "../../utils/errorUtils";
import CareScreenerCard from "@components/care-screener/CareScreenerCard";
import {
  DEFAULT_INSURANCE_CARD_TITLE,
  resolveInsuranceCardDescription
} from "./insuranceCardCopy";

type Props = {
  onSubmitCard: (front: FileData, back: FileData | null) => Promise<void>;
  onSubmitManual?: (memberId: string, groupNumber: string) => Promise<void>;
  onSkip?: () => void;
  skipLabel?: string;
  headerComponent?: React.ReactNode;
  title?: string;
  description?: string;
};

export default function AddInsuranceCard({
  onSubmitCard,
  onSubmitManual,
  onSkip,
  skipLabel = "I'll do this later",
  headerComponent,
  title = DEFAULT_INSURANCE_CARD_TITLE,
  description
}: Props) {
  const [cardFrontImage, setCardFrontImage] = useState<FileData | null>(null);
  const [cardBackImage, setCardBackImage] = useState<FileData | null>(null);
  const [loading, setLoading] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [memberId, setMemberId] = useState("");
  const [groupNumber, setGroupNumber] = useState("");
  const { showError } = useGlobalContext();

  const handleCardSubmit = async () => {
    if (!cardFrontImage) return;
    try {
      setLoading(true);
      logEvent("addInsuranceCardSubmitted");
      await onSubmitCard(cardFrontImage, cardBackImage);
    } catch (error) {
      logEvent("addInsuranceCardError");
      reportError("AddInsuranceCard.handleCardSubmit", error);
      showError();
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = async () => {
    if (!memberId || !groupNumber || !onSubmitManual) return;
    try {
      setLoading(true);
      logEvent("addInsuranceCardManualSubmitted");
      await onSubmitManual(memberId, groupNumber);
    } catch (error) {
      logEvent("addInsuranceCardError");
      reportError("AddInsuranceCard.handleManualSubmit", error);
      showError();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex center>
      <CareScreenerCard>
        <Flex center style={{ margin: 15 }}>
          {headerComponent}
          <Text style={{ marginTop: 10 }} size={24} weight={700} theme="manaBlueDark">
            {title}
          </Text>
          {!showManual && (
            <>
              <img src={InsuranceCardImage} style={styles.image} />
              <Text center color="#212328" weight={400} size={16} style={{ margin: 20, width: 300 }}>
                {resolveInsuranceCardDescription(false, description)}
              </Text>
              <PhotoUploader
                cameraContainerStyle={styles.cameraContainer}
                label="Front of insurance card"
                newFileName="Insurance_Card_Front"
                onFileChange={setCardFrontImage}
              />
              <div
                style={{
                  width: "100%",
                  height: 1,
                  margin: 10,
                  backgroundColor: "#E0E0E0"
                }}
              />
              <PhotoUploader
                cameraContainerStyle={styles.cameraContainer}
                label="Back of insurance card"
                newFileName="Insurance_Card_Back"
                onFileChange={setCardBackImage}
              />
              <Flex row fullWidth center style={styles.buttonContainer}>
                <Flex fullWidth>
                  <Button
                    loading={loading}
                    disabled={!cardFrontImage || loading}
                    theme="manaBlue"
                    style={styles.button}
                    title="Submit"
                    fullWidth
                    onPress={handleCardSubmit}
                  />
                </Flex>
              </Flex>
              {onSubmitManual && !loading && (
                <Text
                  style={{ marginTop: 10 }}
                  link={{ onPress: () => setShowManual(true) }}
                >
                  Enter information manually
                </Text>
              )}
              {onSkip && !loading && (
                <Text
                  style={{ marginTop: 10 }}
                  link={{
                    onPress: () => {
                      logEvent("addInsuranceCardLater");
                      onSkip();
                    }
                  }}
                >
                  {skipLabel}
                </Text>
              )}
            </>
          )}
          {showManual && (
            <>
              <Text center color="#212328" weight={400} size={16} style={{ margin: 20, width: 300 }}>
                {resolveInsuranceCardDescription(true, description)}
              </Text>
              <input
                style={styles.textInput}
                placeholder="Member ID"
                value={memberId}
                onChange={e => setMemberId(e.target.value)}
              />
              <input
                style={styles.textInput}
                placeholder="Group Number"
                value={groupNumber}
                onChange={e => setGroupNumber(e.target.value)}
              />
              <Flex row fullWidth center style={styles.buttonContainer}>
                <Flex fullWidth>
                  <Button
                    loading={loading}
                    disabled={!memberId || !groupNumber || loading}
                    theme="manaBlue"
                    style={styles.button}
                    title="Submit"
                    fullWidth
                    onPress={handleManualSubmit}
                  />
                </Flex>
              </Flex>
              {!loading && (
                <Text
                  style={{ marginTop: 10 }}
                  link={{ onPress: () => setShowManual(false) }}
                >
                  Upload insurance card
                </Text>
              )}
            </>
          )}
        </Flex>
      </CareScreenerCard>
    </Flex>
  );
}

const styles = createStyleSheet({
  image: {
    width: 200,
    height: 200,
    margin: 10,
    objectFit: "cover"
  },
  button: {
    padding: 15,
    width: "100%"
  },
  buttonContainer: {
    marginTop: 20
  },
  cameraContainer: {
    maxWidth: 800,
    borderRadius: 20
  },
  textInput: {
    width: "100%",
    maxWidth: 300,
    padding: "10px 14px",
    marginBottom: 10,
    border: "1px solid #E0E0E0",
    borderRadius: 8,
    fontSize: 16
  }
});
